import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { GraduationCap, ArrowLeft, Loader2, Clock, Trash2, Pencil, Sparkles, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { getGeneration, deleteGeneration } from "@/lib/history.functions";
import { generateOutline } from "@/lib/smartessay.functions";
import { OutlineView } from "@/components/OutlineView";
import type { Outline } from "@/lib/smartessay-types";
import { EssayPdfButton } from "@/components/EssayPdfButton";

type Row = {
  id: string;
  created_at: string;
  title: string;
  assignment_type: "essay" | "summary" | "key_concepts" | "review" | "tutor";
  prompt: string | null;
  source_text: string | null;
  output: Outline | TutorOutput | Record<string, unknown>;
};

type TutorOutput = {
  kind: "tutor";
  brain_work_score: number;
  positive_note: string;
  messages: { role: "user" | "assistant"; content: string }[];
  source_title: string | null;
};

function isTutorOutput(o: unknown): o is TutorOutput {
  return !!o && typeof o === "object" && (o as { kind?: string }).kind === "tutor";
}

export const Route = createFileRoute("/history/$id")({
  head: () => ({ meta: [{ title: "Outline — SmartEssay" }] }),
  component: HistoryDetailPage,
});

function HistoryDetailPage() {
  const navigate = useNavigate();
  const { id } = Route.useParams();
  const get = useServerFn(getGeneration);
  const del = useServerFn(deleteGeneration);
  const regen = useServerFn(generateOutline);
  const [checking, setChecking] = useState(true);
  const [row, setRow] = useState<Row | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        navigate({ to: "/auth" });
        return;
      }
      setChecking(false);
      get({ data: { id } })
        .then((r) => setRow(r as Row))
        .catch((e) => toast.error((e as Error).message || "Couldn't load outline."));
    });
  }, [navigate, get, id]);

  function openEdit() {
    if (!row) return;
    setEditTitle(row.title);
    setEditPrompt(row.prompt ?? "");
    setEditing(true);
  }

  async function onRegenerate() {
    if (!row) return;
    if (!editTitle.trim()) {
      toast.error("Title is required.");
      return;
    }
    setRegenerating(true);
    try {
      const result = await regen({
        data: {
          assignment_type: row.assignment_type,
          title: editTitle.trim(),
          prompt: editPrompt.trim() || undefined,
          source_text: row.source_text ?? undefined,
        },
      });
      toast.success("New outline generated.");
      navigate({ to: "/history/$id", params: { id: result.id } });
    } catch (e) {
      toast.error((e as Error).message || "Couldn't regenerate.");
    } finally {
      setRegenerating(false);
    }
  }

  async function onDelete() {
    if (!confirm("Delete this outline? This can't be undone.")) return;
    setDeleting(true);
    try {
      await del({ data: { id } });
      toast.success("Outline deleted.");
      navigate({ to: "/history" });
    } catch (e) {
      toast.error((e as Error).message || "Couldn't delete.");
      setDeleting(false);
    }
  }


  if (checking) return <div className="grid min-h-screen place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-hero-gradient">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link to="/" className="flex items-center gap-2 font-display text-lg font-semibold">
          <GraduationCap className="h-5 w-5 text-primary" /> SmartEssay
        </Link>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/history"><ArrowLeft className="mr-1.5 h-4 w-4" /> All outlines</Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={openEdit} disabled={!row || editing}>
            <Pencil className="mr-1.5 h-4 w-4" /> Edit prompt
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} disabled={deleting} className="text-destructive hover:bg-destructive/10 hover:text-destructive">
            {deleting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1.5 h-4 w-4" />}
            Delete
          </Button>
        </div>

      </header>

      <main className="mx-auto max-w-3xl px-6 pb-24">
        {!row ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading outline…
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">{row.title}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {new Date(row.created_at).toLocaleString()}
              </span>
              <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-accent-foreground">
                {row.assignment_type.replace("_", " ")}
              </span>
            </div>

            {row.prompt && !editing && (

              <div className="mt-4 rounded-lg border border-border bg-card p-4 text-sm">
                <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Prompt</div>
                <p className="mt-1 whitespace-pre-wrap">{row.prompt}</p>
              </div>
            )}
            {editing && (
              <div className="mt-4 rounded-xl border border-primary/40 bg-card p-5 shadow-soft">
                <div className="mb-3 flex items-center justify-between">
                  <div className="inline-flex items-center gap-2 text-sm font-semibold">
                    <Pencil className="h-4 w-4 text-primary" /> Edit prompt & regenerate
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={regenerating}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Title</label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="mt-1"
                  disabled={regenerating}
                />
                <label className="mt-3 block text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Prompt
                </label>
                <Textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  rows={5}
                  className="mt-1"
                  placeholder="Update the prompt and we'll generate a fresh outline using the same source text."
                  disabled={regenerating}
                />
                {row.source_text && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    The original source text will be reused for the new outline.
                  </p>
                )}
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={regenerating}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={onRegenerate} disabled={regenerating}>
                    {regenerating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                    Regenerate outline
                  </Button>
                </div>
              </div>
            )}


            {row.source_text && (
              <details className="mt-3 rounded-lg border border-border bg-card p-4 text-sm">
                <summary className="cursor-pointer text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Source text
                </summary>
                <p className="mt-2 whitespace-pre-wrap text-foreground">{row.source_text}</p>
              </details>
            )}
            <div className="mt-6">
              {isTutorOutput(row.output) ? (
                <TutorSessionView output={row.output} />
              ) : row.assignment_type === "essay" || row.assignment_type === "summary" || row.assignment_type === "key_concepts" ? (
                <>
                  <EssayPdfButton
                    outline={row.output as Outline}
                    title={row.title}
                    prompt={row.prompt ?? undefined}
                    assignmentType={row.assignment_type}
                    level="advanced"
                    tone="academic"
                    wordMin={500}
                    wordMax={800}
                  />
                  <OutlineView outline={row.output as Outline} />
                </>
              ) : (
                <p className="text-sm text-muted-foreground">This item can't be displayed here.</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function TutorSessionView({ output }: { output: TutorOutput }) {
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-gold/40 bg-card p-5 shadow-soft">
        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Brain Work Score</div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-display text-5xl font-semibold text-primary">{output.brain_work_score}%</span>
          <span className="text-sm text-muted-foreground">of the thinking was the student</span>
        </div>
        {output.positive_note && <p className="mt-3 text-sm text-foreground">{output.positive_note}</p>}
      </div>
      <div className="rounded-2xl border border-border bg-card p-5 shadow-soft">
        <div className="mb-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">Transcript</div>
        <div className="space-y-4">
          {output.messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div
                className={
                  m.role === "user"
                    ? "max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-sm text-primary-foreground"
                    : "max-w-[90%] whitespace-pre-wrap text-sm text-foreground"
                }
              >
                {m.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
